import { Download, FileText, BookOpen, BarChart3, DollarSign, Clock, Star } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';

const reports = [
  {
    id: 'operational-risk',
    icon: BookOpen,
    color: 'bg-slate-900',
    iconColor: 'text-white',
    file: '/research/tapelligence-operational-risk-report.pdf',
    filename: 'tapelligence-operational-risk-report.pdf',
    pages: '10+',
  },
  {
    id: 'detection-delay',
    icon: Clock,
    color: 'bg-red-600',
    iconColor: 'text-white',
    file: '/research/detection-delay-risk-escalation.pdf',
    filename: 'detection-delay-risk-escalation.pdf',
    pages: '8',
  },
  {
    id: 'incident-frequency',
    icon: BarChart3,
    color: 'bg-[#0077FF]',
    iconColor: 'text-white',
    file: '/research/incident-frequency-benchmarks.pdf',
    filename: 'incident-frequency-benchmarks.pdf',
    pages: '8',
  },
  {
    id: 'financial-modeling',
    icon: DollarSign,
    color: 'bg-emerald-700',
    iconColor: 'text-white',
    file: '/research/financial-risk-modeling.pdf',
    filename: 'financial-risk-modeling.pdf',
    pages: '8',
  },
  {
    id: 'airport-hazard',
    icon: BarChart3,
    color: 'bg-[#0077FF]/80',
    iconColor: 'text-white',
    file: '/research/Airport_Hazard_Risk_and_Loss_Analysis_Report.pdf',
    filename: 'Airport_Hazard_Risk_and_Loss_Analysis_Report.pdf',
    pages: '12+',
  },
  {
    id: 'automotive-reputation',
    icon: Star,
    color: 'bg-slate-800',
    iconColor: 'text-amber-400',
    file: '/research/Tapelligence_Automotive_Online_Reputation_Dealer_Report.pdf',
    filename: 'Tapelligence_Automotive_Online_Reputation_Dealer_Report.pdf',
    pages: '14+',
    date: 'September 2026',
    industry: true,
  },
];

const ReportCard = ({ report, t }) => {
  const Icon = report.icon;
  const title = t(`riskData.reports.${report.id}.title`);
  const description = t(`riskData.reports.${report.id}.description`);
  const industry = report.industry ? t(`riskData.reports.${report.id}.industry`) : null;
  const highlights = report.industry ? t(`riskData.reports.${report.id}.highlights`) : null;
  const ctaText = report.industry ? t(`riskData.reports.${report.id}.cta`) : null;
  const dateStr = report.date || 'March 2026';

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 p-6 flex flex-col h-full hover:border-slate-300 hover:shadow-sm transition-all"
      data-testid={`report-card-${report.id}`}
    >
      <div className="flex items-start gap-4 mb-4">
        <div className={`w-11 h-11 ${report.color} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-5 h-5 ${report.iconColor}`} />
        </div>
        <div className="flex-1 min-w-0">
          {industry && (
            <span className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1.5 block">{industry}</span>
          )}
          <h3 className="text-sm font-bold text-slate-900 leading-snug mb-1">{title}</h3>
          <p className="text-xs text-slate-500">PDF &mdash; {report.pages} pages &mdash; {dateStr}</p>
        </div>
      </div>
      <p className="text-sm text-slate-600 leading-relaxed mb-5">{description}</p>

      {highlights && (
        <div className="border-t border-slate-100 pt-4 mb-5 space-y-3">
          {highlights.map((h, i) => (
            <div key={i}>
              <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-0.5">{h.label}</p>
              <p className="text-lg font-bold text-slate-900 leading-tight">{h.value}</p>
              {h.note && <p className="text-[10px] text-slate-400 mt-0.5">{h.note}</p>}
            </div>
          ))}
        </div>
      )}

      <div className="mt-auto">
        <a
          href={report.file}
          download={report.filename}
          className="inline-flex items-center justify-center gap-2 w-full px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-sm font-semibold rounded-lg transition-colors"
          data-testid={`download-btn-${report.id}`}
        >
          <Download className="w-4 h-4" />
          {ctaText || t('riskData.downloadReport')}
        </a>
      </div>
    </div>
  );
};

const OperationalRiskDataPage = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="pt-36 pb-16 bg-slate-50 border-b border-slate-200" data-testid="risk-data-hero">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-xs font-semibold text-[#0077FF] uppercase tracking-wider mb-3 block" data-testid="risk-data-label">
              {t('riskData.hero.label')}
            </span>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-5 leading-tight" data-testid="risk-data-title">
              {t('riskData.hero.title')}
            </h1>
            <p className="text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl" data-testid="risk-data-subtitle">
              {t('riskData.hero.subtitle')}
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section-padding" data-testid="risk-data-overview">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4" data-testid="overview-title">
              {t('riskData.overview.title')}
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              {t('riskData.overview.body')}
            </p>
          </div>

          {/* Report Grid */}
          <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto" data-testid="reports-grid">
            {reports.slice(0, reports.length % 2 === 0 ? reports.length : reports.length - 1).map((report) => (
              <ReportCard key={report.id} report={report} t={t} />
            ))}
          </div>
          {reports.length % 2 !== 0 && (
            <div className="grid sm:grid-cols-2 gap-6 max-w-4xl mx-auto mt-6">
              <div className="sm:col-start-1 sm:col-end-3 flex justify-center">
                <div className="w-full sm:max-w-[calc(50%-0.75rem)]">
                  <ReportCard report={reports[reports.length - 1]} t={t} />
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* What This Data Shows */}
      <section className="section-padding border-t border-slate-200" data-testid="data-insight-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto">
            <h3 className="text-sm font-bold text-slate-900 mb-4" data-testid="data-insight-title">What this data shows</h3>
            <div className="space-y-3">
              {[
                'Detection delay is the primary driver of escalation',
                'Most environments lack real-time incident visibility',
                'Small delays create disproportionate operational impact',
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 bg-slate-400 rounded-full mt-2 flex-shrink-0" />
                  <p className="text-sm text-slate-600">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Source Authority Logos */}
      <section className="section-padding border-t border-slate-200" data-testid="source-logos">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto text-center">
            <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-8">Data sourced from</p>
            <div className="flex flex-wrap items-center justify-center gap-10 md:gap-16">
              {[
                { abbr: 'NSC', name: 'National Safety Council' },
                { abbr: 'OSHA', name: 'Occupational Safety & Health Administration' },
                { abbr: 'BLS', name: 'Bureau of Labor Statistics' },
                { abbr: 'NFSI', name: 'National Floor Safety Institute' },
              ].map((src, i) => (
                <div key={i} className="text-center" data-testid={`source-logo-${src.abbr.toLowerCase()}`}>
                  <p className="text-lg md:text-xl font-bold text-slate-700 tracking-tight">{src.abbr}</p>
                  <p className="text-[10px] text-slate-400 mt-0.5">{src.name}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Methodology Note */}
      <section className="section-padding bg-slate-50 border-t border-slate-200" data-testid="methodology-section">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-white rounded-lg border border-slate-200 flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-slate-500" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-900 mb-2" data-testid="methodology-title">
                  {t('riskData.methodology.title')}
                </h3>
                <p className="text-sm text-slate-600 leading-relaxed mb-3">
                  {t('riskData.methodology.body')}
                </p>
                <p className="text-xs text-slate-400 italic">
                  {t('riskData.methodology.sources')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OperationalRiskDataPage;
